const DEFAULT_API_BASE_URL = '/api'
const DEFAULT_SUBSCRIPTION_ACCESS_BASE_URL = 'https://sub.astride.pro/'

const CONTROL_CHARACTERS_REGEX = /[\u0000-\u001F\u007F]/g
const HTTP_PROTOCOLS = new Set(['http:', 'https:'])

const normalizeStringValue = (value: string): string => {
  return String(value || '').trim().replace(CONTROL_CHARACTERS_REGEX, '')
}

const removeTrailingSlash = (value: string): string => {
  if (value === '/') {
    return value
  }

  return value.replace(/\/+$/, '')
}

const ensureTrailingSlash = (value: string): string => {
  return value.endsWith('/') ? value : `${value}/`
}

const normalizeHttpUrl = (value: string): string | null => {
  const normalized = normalizeStringValue(value)
  if (!normalized) {
    return null
  }

  try {
    const parsed = new URL(normalized)
    if (!HTTP_PROTOCOLS.has(parsed.protocol)) {
      return null
    }

    return removeTrailingSlash(parsed.toString())
  } catch {
    return null
  }
}

const normalizeApiBaseUrl = (value: string): string | null => {
  const normalized = normalizeStringValue(value)
  if (!normalized) {
    return null
  }

  if (normalized.startsWith('/')) {
    return removeTrailingSlash(normalized)
  }

  return normalizeHttpUrl(normalized)
}

const resolveClientEnv = (
  value: string | undefined,
  fallback: string,
  normalizer: (raw: string) => string | null,
): string => {
  if (!value) {
    return fallback
  }

  const normalized = normalizer(value)
  return normalized || fallback
}

const subscriptionAccessBaseUrl = resolveClientEnv(
  import.meta.env.VITE_SUBSCRIPTION_ACCESS_BASE_URL,
  DEFAULT_SUBSCRIPTION_ACCESS_BASE_URL,
  (rawValue) => {
    const normalized = normalizeHttpUrl(rawValue)
    return normalized ? ensureTrailingSlash(normalized) : null
  },
)

export const envConfig = {
  apiBaseUrl: resolveClientEnv(import.meta.env.VITE_API_BASE_URL, DEFAULT_API_BASE_URL, normalizeApiBaseUrl),
  subscriptionAccessBaseUrl,
  publicAppBaseUrl: resolveClientEnv(import.meta.env.VITE_PUBLIC_APP_BASE_URL, '', (rawValue) => {
    const normalized = normalizeHttpUrl(rawValue)
    return normalized ? removeTrailingSlash(normalized) : null
  }),
  authPortalBaseUrl: resolveClientEnv(import.meta.env.VITE_AUTH_PORTAL_BASE_URL, '/auth/', (rawValue) => {
    const normalized = normalizeStringValue(rawValue)
    if (!normalized) {
      return null
    }

    if (normalized.startsWith('/')) {
      return ensureTrailingSlash(normalized)
    }

    const absolute = normalizeHttpUrl(normalized)
    return absolute ? ensureTrailingSlash(absolute) : null
  }),
} as const
